'use client'

/**
 * «Monster block»: un muretto tradizionale di mattoni rossi con le **fughe
 * bianche sfalsate**, posati in sequenza dall'angolo in basso a destra fino in
 * alto a destra. Poi il muro esplode **dal centro** e la scritta MONSTER BLOCK
 * pulsa tre volte: al terzo battito la scena si chiude e il punteggio torna
 * leggibile.
 *
 * PERCHÉ PROCEDURALE: la card va da 190 a 672 px e cambia misura nei siti figli,
 * il testo deve restare nitido a ogni scala e partire dall'evento, non da un
 * file. Il disegno (posizioni, ritardi, direzioni dell'esplosione) è calcolato
 * qui in modo deterministico: nessun `random`, il risultato è identico a ogni
 * esecuzione.
 *
 * Tempi (vedi `globals.css`): mattoni 0→1,2 s, esplosione 1,25→1,7 s, testo da
 * 1,8 s per tre pulsazioni da 550 ms → fine a 3,45 s. `BLOCK_SCENE_MS` in
 * `useBlockScene` chiude la scena a 3,6 s.
 */

/** Corsi (righe) del muro, dal basso verso l'alto. */
const ROWS = 4
/** Mattoni per corso: il muro esce dal riquadro, i bordi si tagliano sul frame. */
const PER_ROW = 4
/** Durata della posa di un mattone: il ritardo di ognuno è indice × questo. */
const LAY_MS = 70

interface Brick {
  key: string
  left: number
  top: number
  width: number
  height: number
  /** Ordine di posa (0 = primo mattone). */
  order: number
  /** Direzione dell'esplosione, dal centro del muro. */
  dx: number
  dy: number
  rot: number
}

function buildBricks(): Brick[] {
  // Un mattone e mezzo di margine: il muro sborda a sinistra (tagliato dal
  // riquadro) e resta «continuo», come un muro vero.
  const brickW = 100 / 3.5
  const brickH = 100 / ROWS
  const bricks: Brick[] = []
  let order = 0

  for (let row = 0; row < ROWS; row += 1) {
    // Corso 0 = in basso; i corsi dispari partono sfalsati di mezzo mattone.
    const stagger = row % 2 === 0 ? 0 : brickW / 2
    const top = 100 - (row + 1) * brickH
    // Posizioni da destra verso sinistra; i corsi dispari si posano da sinistra
    // verso destra, così **l'ultimo mattone** del corso in alto cade a destra.
    const xs = Array.from({ length: PER_ROW }, (_, k) => 100 - (k + 1) * brickW + stagger)
    const sequence = row % 2 === 0 ? xs : [...xs].reverse()

    for (const x of sequence) {
      const cx = x + brickW / 2
      const cy = top + brickH / 2
      // Vettore dal centro del muro al centro del mattone, allungato: i mattoni
      // volano via radialmente, come un'esplosione dal centro.
      const vx = (cx - 50) * 3.2
      const vy = (cy - 50) * 3.2
      bricks.push({
        key: `${row}-${x.toFixed(1)}`,
        left: x,
        top,
        width: brickW,
        height: brickH,
        order: order++,
        dx: Math.round(vx),
        dy: Math.round(vy),
        rot: Math.round(vx / 2),
      })
    }
  }
  return bricks
}

const BRICKS = buildBricks()

export function MonsterBlockScene({ side }: { side: 'home' | 'away' }) {
  return (
    <div
      aria-hidden
      data-block-scene={side}
      className="lv-mb pointer-events-none absolute inset-0 z-[4] overflow-hidden"
    >
      <div className="lv-mb-wall">
        {BRICKS.map((brick) => (
          <span
            key={brick.key}
            className="lv-mb-brick"
            style={{
              left: `${brick.left}%`,
              top: `${brick.top}%`,
              width: `${brick.width}%`,
              height: `${brick.height}%`,
              ['--i' as string]: brick.order,
              ['--lay' as string]: `${brick.order * LAY_MS}ms`,
              ['--dx' as string]: `${brick.dx}px`,
              ['--dy' as string]: `${brick.dy}px`,
              ['--rot' as string]: `${brick.rot}deg`,
            }}
          />
        ))}
      </div>
      <p className="lv-scene-text lv-mb-text">
        Monster
        <br />
        Block
      </p>
    </div>
  )
}

export default MonsterBlockScene
